import { observer } from 'mobx-react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Button, ConfirmationButton, ConfirmDialog, Paper, PDFPreview, SelectField, TextField } from '@roc/ui';
import { Box, Checkbox, Chip, Collapse, FormControlLabel, Grid, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@material-ui/core';
import { AddBoxOutlined, IndeterminateCheckBoxOutlined, OutlinedFlag, UnfoldLess, UnfoldMore } from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { Layout } from '@roc/feature-app-core';
import { InsuranceQuotesTable } from './insuranceQuotesTable';
import { InsuranceInformationReviewBaseStore } from '../stores';
import { debounce, LoanSubType, isFundedLoan } from '@roc/feature-utils';
import { useMemo } from 'react';
import { Document } from '@roc/feature-types';
import { WithDocumentActions } from '@roc/feature-documents';

const useStyles = makeStyles((theme: Theme) => ({
  titleRow: {
    height: '1.8em',
    borderLeft: '1px solid',
    borderLeftColor: '#e6f6fe',
    borderRight: '1px solid',
    borderRightColor: '#e6f6fe'
  },
  headerCell: {
    fontWeight: 'bold',
    padding: 0,
    textAlign: 'left',
  },
  revisionHeaderCell: {
    fontWeight: 'bold',
    padding: '5px',
    textAlign: 'left',
  },
  headerCellCenter: {
    textAlign: 'center'
  },
  tableContainerWrapper: {
    padding: '8px',
    backgroundColor: '#F0F0F0',
    width: '100%',
    boxSizing: 'border-box',
  },
  tableContainer: {
    border: '1px #e0e0e0 solid',
    backgroundColor: '#FFF',
    width: '100%',
    boxSizing: 'border-box',
  },
  table: {
    margin: 0,
    padding: 0,
    '& td': {
      padding: '5px 0',
    },
    '& th:first-child, td[rowSpan], $addNewItemRow td, td:first-child': {
      paddingLeft: theme.spacing(2),
      paddingRight: '3px',
    },
    '& table tr:last-child td, & table td[rowSpan]': {
      borderBottom: 'none',
    },
    '& input': {
      padding: '12px',
      fontSize: '0.875rem'
    }
  },
  subSection: {
    padding: '20px 20px 5px 20px'

  },
  subSectionTitle: {
    fontSize: '1.2rem',
  },
  subTitle: {
    fontSize: '0.9rem',
    color: 'gray'
  },
  expandMainSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  tableHeader: { padding: '15px', borderBottom: '1.5px solid #E0E0E0FF' },
  button: {
    fontSize: '16px',
    fontFamily: 'Open Sans,Lato,sans-serif',
    textTransform: 'capitalize',
    margin: theme.spacing(1, 0, 1, 0),
    width: '240px',
    color: '#00B2FF',
    backgroundColor: '#E2F6FF',
    borderColor: '#00B2FF'
  },
  flag: {
    fontSize: '25px'
  },
  title: {
    color: '#757575',
    fontSize: 20
  },
  reviewSectionContainer: {
    padding: '20px 0px',
  },
  iconWhite: {
    color: '#FFF'
  },
  successStyle: {
    color: theme.palette.success.dark
  },
  buttonError: {
    backgroundColor: theme.palette.error.main,
    color: '#FFF'
  },
  chipError: {
    color: '#FFF',
    backgroundColor: theme.palette.error.main,
    borderRadius: '8px',
    borderColor: 'transparent',
    width: '55px',
    "&&:hover": {
      backgroundColor: theme.palette.error.main
    },
    "&&:focus": {
      backgroundColor: theme.palette.error.main
    }
  },
  chipSuccess: {
    color: theme.palette.success.dark,
    backgroundColor: '#FFF',
    borderRadius: '8px',
    borderColor: theme.palette.success.dark,
    width: '55px',
  },
  itemDisplayNameCell: {
    width: '15%',
    textAlign: 'left',
    paddingTop: '5px',
    paddingBottom: '5px',
  },
  itemRevisionDisplayNameCell: {
    width: '30%',
    textAlign: 'left',
    padding: '15px !important'
  },
  revisionCommentPreview: {
    width: '70%',
    textAlign: 'left',
    padding: '15px 5px !important'
  },
  dataCell: {
    width: '20%',
    textAlign: 'left',
    paddingTop: '5px',
    paddingBottom: '5px',
  },
  needsRevisionFlagCell: {
    width: '10%',
    textAlign: 'center',
  },
  sendForRevisionCell: {
    width: '10%',
    textAlign: 'center',
  },
  cell: {
    width: '20%',
    textAlign: 'center',
    paddingTop: '5px',
    paddingBottom: '5px',
  },
  expectedValueLabel: {
    fontWeight: 'bold',
    padding: '0 10px'
  },
  previewFieldName: {
    padding: '5px 0'
  },
  previewNewValue: {
    color: '#CD5C5C',
    fontWeight: 'bold',
  },
  noBorderCell: {
    border: 0,
  },
  reviewButtons: {
    minWidth: '185px',
  },
  disabled: {
    color: 'gray'
  }
}));

export const columns = [
  { name: 'Fields', mapping: 'itemDisplayName' },
  { name: 'Extracted Data', mapping: 'data' },
  { name: 'Expected Value', mapping: 'expectedValue' },
  { name: 'Flagged', mapping: 'needsRevisionFlag', className: 'headerCellCenter' },
  { name: 'Resolution?', mapping: 'sendForRevision', className: 'headerCellCenter' },
  { name: 'Reviewer Comment', mapping: 'reviewerComment', className: 'headerCellCenter' }
];

export const previewColumns = [
  { name: 'Fields', mapping: 'itemDisplayName', className: null },
  { name: 'Current Data', mapping: 'currentData', className: null },
  { name: 'New Data', mapping: 'newData', className: null },
  { name: 'Accept', mapping: 'selectedForReplacement', className: 'headerCellCenter' },
];

export const sendForRevisionColumns = [
  { name: 'Item', mapping: 'itemRevisionDisplayName' },
  { name: 'Reviewer Comment', mapping: 'reviewerCommentRevision' }
];

const parseTitleSubtitle = (data?: string | null) => {
  if (!data) return { title: '', subTitle: null as string | null };

  const [title, subTitle] = data.split('|*|', 2).map(s => s?.trim());
  return subTitle ? { title, subTitle } : { title: data, subTitle: null };
};

const ReviewTableItemValue = observer((props: { coverage: string, item: any, column: any, store: InsuranceInformationReviewBaseStore, readOnlyItem: boolean }) => {
  const { item, column, store, coverage, readOnlyItem } = props;
  const { mapping } = column;
  const value = item[mapping];
  const classes = useStyles();

  const addEvent = useMemo(() =>
    debounce((value: any) => store.addEvent({ coverage, mapping, sectionName: item.sectionName, itemDisplayName: item.itemDisplayName, value }), 750), []);

  const handlChangeDebounce = (value) => {
    store.handleItemChange(coverage, mapping, item.uniqueName, value);
    addEvent(value);
  }

  const handleAndLogChange = (value) => {
    store.handleItemChange(coverage, mapping, item.uniqueName, value);
    store.addEvent({ coverage, mapping, sectionName: item.sectionName, itemDisplayName: item.itemDisplayName, value });
  }

  const handleRevisionComment = async value => {
    console.log("handleRevisionComment", value);
    if (value) {
      store.setRevisionComment(coverage, item);
    }
  }

  const handleNeedsRevisionFlag = (value) => {
    handleAndLogChange(value);
    if (value) {
      handleSendForRevision(value);
    }
  }

  const handleSendForRevision = (value: boolean) => {
    store.handleItemChange(coverage, 'sendForRevision', item.uniqueName, value);
    handleAndLogChange(value);
    handleRevisionComment(value);
  }

  switch (mapping) {
    case 'data':
      return <TextField
        testId="data"
        variant="outlined"
        standaloneLabel
        value={value}
        fullWidth
        disabled={store.readOnlyForm || readOnlyItem}
        onChange={e => handlChangeDebounce(e.target.value)}
      />
    case 'reviewerComment':
      return <TextField
        testId="reviewerComment"
        variant="outlined"
        standaloneLabel
        value={value}
        multiline
        maxRows={4}
        fullWidth
        disabled={store.readOnlyForm || readOnlyItem}
        onChange={e =>
          store.handleItemChange(coverage, mapping, item.uniqueName, e.target.value)
        }
      />
    case 'needsRevisionFlag':
      const className = !!value ? classes.chipError : classes.chipSuccess;
      return <Chip
        variant="outlined"
        size="small"
        icon={<OutlinedFlag className={!!value ? classes.iconWhite : classes.successStyle} />}
        label={!!value ? 'Yes' : 'No'}
        className={className}
        style={{
          fontWeight: 0,
        }}
        clickable={!store.readOnlyForm && !readOnlyItem}
        onClick={(e) => {
          if (!store.readOnlyForm && !readOnlyItem) {
            handleNeedsRevisionFlag(!value);
          }
        }}
      />
    case 'sendForRevision':
      return <Checkbox
        checked={value}
        size='small'
        disabled={store.readOnlyForm || readOnlyItem}
        onChange={e => handleSendForRevision(e.target.checked)}
        color="secondary"
      />
    case 'expectedValue':
      return <Typography variant="body2" className={classes.expectedValueLabel}>{value}</Typography>
    default:
      return <Typography variant="body2">{value}</Typography>
  }
});

const ReviewTableSection = observer(({ coverage, sectionData, store, readOnlySection, showOnlyFlagged }
  : { coverage: string, sectionData: any, store: InsuranceInformationReviewBaseStore, readOnlySection: boolean, showOnlyFlagged: boolean }) => {
  const { sectionName, items } = sectionData;
  const classes = useStyles();

  const getClassNameByColumn = (mapping) => {
    switch (mapping) {
      case 'itemDisplayName':
        return classes.itemDisplayNameCell;
      case 'expectedValue':
      case 'data':
        return classes.dataCell;
      case 'needsRevisionFlag':
        return classes.needsRevisionFlagCell;
      case 'sendForRevision':
        return classes.sendForRevisionCell;
      default:
        return classes.cell;
    }
  }

  const showItemBasedOnFlag = (item) => {
    return (showOnlyFlagged) ? item.needsRevisionFlag == showOnlyFlagged : true;
  }

  return (
    <Paper style={{ border: '1.5px solid #E0E0E0FF' }}>
      <Grid item xs={12} className={classes.tableHeader}>
        <Grid item>
          <Typography>{sectionName}</Typography>
        </Grid>
      </Grid>
      <Grid item xs={12} className={classes.expandMainSection}>
        <Grid container className={classes.tableContainerWrapper} xs={12}>
          <TableContainer className={classes.tableContainer}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.titleRow}>
                  {columns.map(column => {
                    return (
                      <TableCell className={`${classes.headerCell} ${classes[column.className] ?? ''}`}> {column.name} </TableCell>
                    )
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {items
                  .filter((item) => showItemBasedOnFlag(item))
                  .map((item, index) => (
                    <TableRow key={item.uniqueName || index}>
                      {columns.map(column => {
                        item = { ...item, sectionName: sectionName };
                        return (
                          <TableCell
                            className={getClassNameByColumn(column.mapping)}
                            align="center"
                          >
                            <ReviewTableItemValue
                              coverage={coverage}
                              item={item}
                              column={column}
                              store={store}
                              readOnlyItem={readOnlySection}
                            />
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Paper>
  )
});


const SendForRevisionTableSection = observer(({ coverage, store }
  : { coverage: string, store: InsuranceInformationReviewBaseStore }) => {
  const classes = useStyles();

  const revisionItems = useMemo(() => store.getRevisionItemsForCoverage(coverage), [store.insuranceReviewData[coverage]]);

  const getClassNameByColumn = (mapping) => {
    switch (mapping) {
      case 'itemRevisionDisplayName':
        return classes.itemRevisionDisplayNameCell;
      case 'reviewerCommentRevision':
        return classes.revisionCommentPreview;
      default:
        return classes.cell;
    }
  }

  return (
    <Paper style={{ border: '1.5px solid #E0E0E0FF' }}>
      <Grid container item className={classes.tableContainerWrapper} xs={12}>
        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow className={classes.titleRow}>
                {sendForRevisionColumns.map(column => {
                  return (
                    <TableCell className={classes.revisionHeaderCell}> {column.name} </TableCell>
                  )
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {revisionItems.map((item) => (
                <TableRow>
                  {sendForRevisionColumns.map(column => {
                    return (
                      <TableCell
                        className={getClassNameByColumn(column.mapping)}
                        align="center"
                      >
                        <ReviewTableItemValue
                          coverage={coverage}
                          item={item}
                          column={column}
                          store={store}
                          readOnlyItem={false}
                        />
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Paper>
  )
});

const ReviewTable = observer(({ store }
  : { store: InsuranceInformationReviewBaseStore }) => {
  const classes = useStyles();
  const [showOnlyFlagged, setShowOnlyFlagged] = useState(true);

  const sectionsWithFlaggedItems = Object.keys(store.insuranceReviewData)
    .filter(key =>
      store.insuranceReviewData[key].some(section =>
        section.items.some(item => item.needsRevisionFlag)
      )
    )
    .reduce((acc, key) => {
      acc[key] = store.insuranceReviewData[key];
      return acc;
    }, {});
  const sectionsToShow = showOnlyFlagged ? sectionsWithFlaggedItems : store.insuranceReviewData;

  return (
    <Paper style={{ border: '1.5px solid #E0E0E0FF' }}>
      <Grid container className={classes.reviewSectionContainer}>
        <Grid item container justifyContent="space-between" alignItems="center" style={{ padding: '0 15px 15px 15px', borderBottom: '1.5px solid #E0E0E0FF' }} >
          <Grid item xs={6}>
            <Typography variant="h5">Review Data Points</Typography>
          </Grid>
          <Grid item container xs={6} justifyContent='flex-end' spacing={2}>
            <Grid item>
              <FormControlLabel
                control={
                  <Switch
                    checked={!showOnlyFlagged}
                    onChange={(_, checked) => setShowOnlyFlagged(!checked)}
                    color="primary"
                  />
                }
                label="Show All"
                disabled={store.readOnlyForm}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          {!Object.keys(sectionsToShow).length && (
            <TableRow>
              <TableCell colSpan={10} className={classes.noBorderCell}>
                <Box>
                  No insurance information available
                </Box>
              </TableCell>
            </TableRow>
          )}
          <Grid item xs={12}>
            {Object.keys(sectionsToShow).map((key) => {
              const { title, subTitle } = parseTitleSubtitle(key);

              return (
                <CollapsableSectionData
                  key={key}
                  sectionKey={key}
                  title={title}
                  subTitle={subTitle}
                  store={store}
                  showOnlyFlagged={showOnlyFlagged}
                />
              );
            })}
          </Grid>

        </Grid>
      </Grid>
    </Paper>
  )
});

const SendForRevisionTable = ({ store }
  : { store: InsuranceInformationReviewBaseStore }) => {
  const classes = useStyles();

  return (
    <Paper style={{ border: '1.5px solid #E0E0E0FF' }}>
      <Grid container className={classes.reviewSectionContainer}>
        <Grid item container justifyContent="space-between" alignItems="center" style={{ padding: '0 15px 15px 15px', borderBottom: '1.5px solid #E0E0E0FF' }} >
          <Grid item xs={6}>
            <Typography variant="h5">Send for Review Preview</Typography>
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          {Object.keys(store.insuranceReviewData)
            .filter(key => store.sectionsMarkedForRevision.includes(key))
            .map((key) => {
              const { title, subTitle } = parseTitleSubtitle(key);

              return (<Grid container spacing={2} item className={classes.subSection}>
                <Grid xs={12} item>
                  <Typography className={classes.subSectionTitle}>{title}</Typography>
                  {
                    subTitle && <Typography className={classes.subTitle}>{subTitle}</Typography>
                  }
                </Grid>
                <Grid xs={12} spacing={2} item>
                  <Grid item xs={12}>
                    <SendForRevisionTableSection coverage={key} store={store} />
                  </Grid>
                </Grid>
              </Grid>)
            })}
        </Grid>
      </Grid>
    </Paper>);
}

const CollapsableSectionData = observer(({ sectionKey, store, title, subTitle, showOnlyFlagged }
  : { store: InsuranceInformationReviewBaseStore, title: string, subTitle: string, sectionKey: string, showOnlyFlagged: boolean }) => {
  const [open, setOpen] = useState(false);
  const classes = useStyles();
  const isReadOnly = store.isReadOnlySection(sectionKey);
  const { selectedQuote } = store;

  const sectionsWithFlaggedItems = (store.insuranceReviewData[sectionKey] || []).filter(
    section => section.items.some(item => item.needsRevisionFlag)
  );
  const sectionsToShow = showOnlyFlagged ? sectionsWithFlaggedItems : store.insuranceReviewData[sectionKey];

  const handlClearFlagEvents = async (sectionKey, selectedQuote) => {
    store.addClearFlagButtonEvent(sectionKey, selectedQuote);
    await store.removeFlagForDoc(sectionKey);
  }

  return (
    <Grid container spacing={2} item className={classes.subSection}>
      <Grid item container xs={12} justifyContent='space-between' alignItems='center'>
        <Grid item>
          {open
            ? <IndeterminateCheckBoxOutlined onClick={() => setOpen(false)} style={{ cursor: 'pointer' }} />
            : <AddBoxOutlined onClick={() => setOpen(true)} style={{ cursor: 'pointer' }} />
          }
        </Grid>
        <Grid item xs={9}>
          <Typography className={classes.subSectionTitle + ' ' + (isReadOnly ? classes.disabled : '')}>{title}</Typography>
          {
            subTitle && <Typography className={classes.subTitle}>{subTitle}</Typography>
          }
        </Grid>
        <Grid item xs={2}>
          {isReadOnly && (<ConfirmationButton
            variant="outlined"
            onClick={() => handlClearFlagEvents(sectionKey, selectedQuote)}
            testId="clearFlags"
            color='secondary'
            confirmDialogProps={{
              title: 'Clear Flags',
              body: 'Are you sure you want to clear the flags for this section?',
            }}
            fullWidth
            disabled={store.readOnlyForm}
            size='small'
            startIcon={<OutlinedFlag />}
          >
            CLEAR FLAGS
          </ConfirmationButton>
          )}
        </Grid>
      </Grid>
      {open && <Grid item xs={12}>
        <Grid container spacing={2} item>
          {sectionsToShow?.map(section =>
            <Grid item xs={12}>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <ReviewTableSection coverage={sectionKey} sectionData={section} store={store} showOnlyFlagged={showOnlyFlagged} readOnlySection={isReadOnly} />
              </Collapse>
            </Grid>
          )}
        </Grid>
      </Grid>}
    </Grid>);
});

export type InsuranceInformationReviewFormProps = {
  store: InsuranceInformationReviewBaseStore;
  loanId: string;
  document?: Document;
  showTitle?: boolean;
  onClose: () => void
};

export const InsuranceInformationReviewForm = observer((props: InsuranceInformationReviewFormProps) => {
  const { store, loanId, onClose, document, ...rest } = props;
  const [resolutionModalState, setResolutionModalState] = useState({
    isOpen: false,
    includeNotification: false
  });
  const { insuranceInformationStore, globalStore, quotes } = store;
  const { loanDetails } = insuranceInformationStore;
  const { userFeatures } = globalStore
  const { showAutoSendRevisions } = userFeatures
  const loanStatus = loanDetails?.status;
  const classes = useStyles();

  useEffect(() => {
    if (loanId) {
      insuranceInformationStore.initReviewForm(loanId, null);
      store.getInsuranceQuotesAndSetSelected(loanId);
    }
  }, [store, loanId])

  useEffect(() => {
    if (store.selectedQuote) {
      store.getInsuranceReviewData(store.selectedQuote);
    }
  }, [store, store.selectedQuote]);

  const onCloseModal = () => {
    onClose();
    store.setDefaults();
  }

  const selectedQuoteObj = quotes.find(q => q.insuranceQuoteDetailId === store.selectedQuote);

  return (
    <div style={{ backgroundColor: '#FFF', width: '100%' }}>
      <Layout>
        <Grid container spacing={2} direction='row' alignItems='flex-start'>
          <Grid container item xs={12} alignItems="center" justifyContent="space-between">
            <Grid item xs={6}>
              <SelectField
                standaloneLabel
                label={'Select an insurance / quote to review'}
                name={'selectedQuote'}
                value={store.selectedQuote}
                options={store.quotesOptions}
                onChange={value => store.setSelectedQuote(value)}
                variant="outlined"
                fullWidth
                shrink
                testId="selectedQuote"
                {...rest}
              />
            </Grid>
            {showAutoSendRevisions && (
              <Grid item>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!selectedQuoteObj?.automatedResolutions}
                      onChange={(_, checked) => store.updateAutomatedResolutionForQuote(checked, store.selectedQuote)}
                      color="primary"
                    />
                  }
                  label="Auto-send Revisions"
                />
              </Grid>
            )}
          </Grid>
          <Grid item xs={12}>
            <InsuranceQuotesTable
              title={'Insurance / Quotes'}
              quotes={store.selectedQuotesData}
              emptyMessage={'No insurance information have been added yet'}
              showCollapseExpandAll={false}
              store={store.insuranceInformationStore}
              readOnly={true}
            />
          </Grid>
          <Grid item xs={12}>
            <ReviewTable
              store={store}
            />
          </Grid>
          {store.sectionsMarkedForRevision.length > 0 && <Grid item xs={12}>
            <SendForRevisionTable
              store={store}
            />
          </Grid>}
          {!isFundedLoan(loanStatus) && (
            <Grid container item spacing={1} xs={12} justifyContent="flex-end" style={{ margin: '20px 11px 0 0' }}>
              <Grid container item xs={12} spacing={1} justifyContent="flex-end">
                <Grid item className={classes.reviewButtons}>
                  <WithDocumentActions
                    document={props.document}
                    component={(btnProps) => (
                      <Button
                        {...btnProps}
                        testId="requestResolution"
                        onClick={() => setResolutionModalState({ includeNotification: false, isOpen: true })}
                        variant='contained'
                        className={classes.buttonError}
                        fullWidth
                      >SEND REVISION</Button>
                    )}
                  />
                </Grid>
                {!isFundedLoan(loanStatus) && <Grid item className={classes.reviewButtons}>
                  <WithDocumentActions
                    document={props.document}
                    component={(btnProps) => (
                      <Button
                        {...btnProps}
                        testId="requestResolution"
                        onClick={() => setResolutionModalState({ includeNotification: true, isOpen: true })}
                        variant='contained'
                        className={classes.buttonError}
                        fullWidth
                      >SEND REVISION WITH COMMENTS</Button>
                    )}
                  />
                </Grid>}
                <Grid item className={classes.reviewButtons}>
                  <WithDocumentActions
                    document={props.document}
                    component={(btnProps) => (
                      <ConfirmationButton
                        {...btnProps}
                        variant="contained"
                        onClick={async () => { await store.setReviewComplete(); onCloseModal(); }}
                        testId="confirmReview"
                        color='primary'
                        confirmDialogProps={{
                          title: 'Complete Review',
                          body: 'Are you sure you want to mark this review as completed?',
                        }}
                        fullWidth
                      >
                        REVIEW COMPLETE
                      </ConfirmationButton>
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={1} justifyContent="flex-end" item xs={12}>
                <Grid item className={classes.reviewButtons}>
                  <Button
                    testId="cancelButton"
                    onClick={onCloseModal}
                    variant="outlined"
                    color="primary"
                    fullWidth
                  >
                    CLOSE
                  </Button>
                </Grid>
                <Grid item className={classes.reviewButtons}>
                  <WithDocumentActions
                    document={props.document}
                    component={(buttonProps) => (
                      <Button
                        {...buttonProps}
                        testId="saveReview"
                        onClick={async () => { await store.saveData(); onCloseModal(); }}
                        variant="outlined"
                        color="primary"
                        fullWidth
                      >Save</Button>
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Layout>
      <ConfirmDialog
        open={resolutionModalState.isOpen}
        title={`Request Resolution(s)`}
        body={`Are you sure you want to request a resolution for the selected items?`}
        handleCancel={() => setResolutionModalState({ includeNotification: false, isOpen: false })}
        handleClose={() => setResolutionModalState({ includeNotification: false, isOpen: false })}
        handleConfirm={async () => {
          await store.requestResolutions(resolutionModalState.includeNotification);
          onCloseModal();
        }}
        confirmButtonText={'Yes'}
        cancelButtonText={'No'}
      />
      <PDFPreview
        open={store.insuranceInformationStore.isPdfPreviewModalOpen}
        title={store.insuranceInformationStore.pdfTitle}
        data={store.insuranceInformationStore.pdfData}
        headers={{}}
        fileName={store.insuranceInformationStore.pdfTitle}
        onClose={() => {
          store.insuranceInformationStore.pdfTitle = null;
          store.insuranceInformationStore.pdfData = null;
          store.insuranceInformationStore.isPdfPreviewModalOpen = false;
        }}
      />
    </div>
  );
});
